import { action } from 'mobx'
import { func, type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { Types } from '@/features/legends/editor/ryno-types'
import { RynoEditorViewModel } from '@/features/legends/ryno/RynoEditorViewModel'
import { validateObject } from '@/utils/validx-util'
import { ConfigEditorViewModelBase } from './ConfigEditorViewModelBase'
import { DeadlineRemindersConfigEditorViewModel } from './DeadlineRemindersConfigEditorViewModel'
import type { DeadlineConfig } from './legend-options'

const deadlineTemplate = `
# Using the Time module, add 7 days to the request's submission date.
Time.AddDays(
  # Refers to the request's submission date. We are using
  # this to compute a date relative to when the request was submitted!
  Request.DateSubmitted
  # The amount of days to add to the aforementioned date.
  7
)
`.trim()

/**
 * How the request deadline is computed, and when to remind about it.
 */
export class DeadlineConfigEditorViewModel extends ConfigEditorViewModelBase<DeadlineConfig> {
  private readonly _schema: IValidationSchema<DeadlineConfigEditorViewModel> = {
    expr: [func(() => this.expr.analyze(), 'There are formula errors.')],
    reminder: [validateObject('Deadline Reminders Configuration is invalid')],
  }

  /**
   * The formula that computes the deadline. No formula means no deadline.
   */
  readonly expr: RynoEditorViewModel

  readonly reminder: DeadlineRemindersConfigEditorViewModel

  readonly validation = validationContext(this, this._schema)

  constructor(dependencies: LegendPartDependencies) {
    super(dependencies)
    this.expr = new RynoEditorViewModel({
      getSymbolTable: () => this._symbolTable,
      getNeededType: () => Types.date,
    })
    this.reminder = new DeadlineRemindersConfigEditorViewModel(dependencies)
    this.lifecycle.manage(this.expr)
    this.lifecycle.manage(this.reminder)
  }

  /**
   * Replaces the formula with an example: 7 days from submission.
   */
  @action.bound
  applyTemplate() {
    this.expr.setValue(deadlineTemplate)
  }

  /**
   * Without a deadline config, the reminders keep their state.
   */
  @action
  hydrate(config: DeadlineConfig | undefined) {
    this.expr.setValue(config?.expr)
    if (config) {
      this.reminder.hydrate(config.reminder)
    }
  }

  /** @inheritDoc */
  toJSON(): DeadlineConfig {
    return { expr: this.expr.toJSON(), reminder: this.reminder.toJSON() }
  }
}
